import { isApplianceDistribution } from "@/services/studio-presentation-profile"
import { getStudioAccessToken } from '@/services/auth-provider'

export interface ParsedFigmaFrameReference {
  figmaFileId: string
  figmaNodeId: string
  figmaUrl?: string
}

export interface FigmaTokenValidationResult {
  ok: boolean
  status: number | null
  message: string
  control: FigmaTokenControl
  tokenRef?: string
  tokenLabel?: string
  authenticated?: boolean
}

export interface FigmaTokenServerStoreOptions {
  workspaceId?: string
  fetchImpl?: typeof fetch
}

export type FigmaTokenControlStatus =
  | 'missing'
  | 'needs_confirmation'
  | 'ready'
  | 'expired'
  | 'invalid'
  | 'rate_limited'
  | 'network_error'

export type FigmaTokenControlAction =
  | 'request_token'
  | 'confirm_token'
  | 'continue'
  | 'replace_token'
  | 'retry_later'

export interface FigmaTokenControl {
  status: FigmaTokenControlStatus
  action: FigmaTokenControlAction
  canImport: boolean
  message: string
  statusCode: number | null
  tokenLabel?: string
}

function normalizeFigmaNodeId(value: string): string {
  const decoded = decodeURIComponent(value.trim())
  return decoded.includes(':') ? decoded : decoded.replace(/-/g, ':')
}

function isLikelyFigmaFileId(value: string): boolean {
  return /^[A-Za-z0-9]{10,}$/.test(value.trim())
}

function isLikelyFigmaNodeId(value: string): boolean {
  return /^\d+(?::|-)\d+$/.test(decodeURIComponent(value.trim()))
}

export function parseFigmaFrameReference(
  value: string,
): ParsedFigmaFrameReference | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed)
      const figmaFileId = url.pathname.match(/\/(?:design|file)\/([^/]+)/)?.[1]
      const rawNodeId =
        url.searchParams.get('node-id') ?? url.searchParams.get('node_id')
      if (!figmaFileId || !rawNodeId) return null
      return {
        figmaFileId,
        figmaNodeId: normalizeFigmaNodeId(rawNodeId),
        figmaUrl: trimmed,
      }
    } catch {
      return null
    }
  }

  const [figmaFileId, rawNodeId] = trimmed
    .split(/[\s,]+/)
    .map((part) => part.trim())
    .filter(Boolean)
  if (!figmaFileId || !rawNodeId) return null
  if (!isLikelyFigmaFileId(figmaFileId) || !isLikelyFigmaNodeId(rawNodeId)) {
    return null
  }
  return {
    figmaFileId,
    figmaNodeId: normalizeFigmaNodeId(rawNodeId),
  }
}

export function parseFigmaFrameReferences(
  value: string,
): ParsedFigmaFrameReference[] {
  const trimmed = value.trim()
  if (!trimmed) return []

  const refs: ParsedFigmaFrameReference[] = []
  const seen = new Set<string>()
  const figmaUrlPattern =
    /https?:\/\/(?:www\.)?figma\.com\/(?:design|file)\/[^\s<>"')]+/gi
  for (const match of trimmed.matchAll(figmaUrlPattern)) {
    const parsed = parseFigmaFrameReference(match[0])
    if (!parsed) continue
    const key = `${parsed.figmaFileId}:${parsed.figmaNodeId}`
    if (seen.has(key)) continue
    seen.add(key)
    refs.push(parsed)
  }

  if (refs.length > 0) return refs

  const single = parseFigmaFrameReference(trimmed)
  return single ? [single] : []
}

export function readStoredFigmaAccessToken(): string | undefined {
  return undefined
}

export function saveStoredFigmaAccessToken(token: string): void {
  void token
}

export function clearStoredFigmaAccessToken(): void {
  // Figma source credentials are server-side. Browser storage is not an authority.
}

export function maskFigmaAccessToken(token: string | undefined): string {
  const trimmed = token?.trim()
  if (!trimmed) return 'Not connected'
  if (trimmed.length <= 8) return 'Connected'
  return `${trimmed.slice(0, 4)}...${trimmed.slice(-4)}`
}

export function buildFigmaTokenControlFromStoredToken(
  token: string | undefined,
): FigmaTokenControl {
  const tokenLabel = maskFigmaAccessToken(token)
  if (!token?.trim()) {
    return {
      status: 'missing',
      action: 'request_token',
      canImport: false,
      message: 'Paste a Figma token and confirm it.',
      statusCode: null,
    }
  }
  return {
    status: 'needs_confirmation',
    action: 'confirm_token',
    canImport: false,
    message: 'Confirm the Figma token before import.',
    statusCode: null,
    tokenLabel,
  }
}

export function buildFigmaTokenControlFromServerReference(
  tokenLabel?: string,
): FigmaTokenControl {
  return {
    status: 'ready',
    action: 'continue',
    canImport: true,
    message: 'Figma token confirmed.',
    statusCode: 200,
    tokenLabel,
  }
}

export function buildFigmaTokenControlFromValidation(
  result: Pick<FigmaTokenValidationResult, 'ok' | 'status' | 'message'>,
  token?: string,
  tokenLabelOverride?: string,
): FigmaTokenControl {
  const tokenLabel = tokenLabelOverride ?? maskFigmaAccessToken(token)
  if (result.ok) {
    return {
      status: 'ready',
      action: 'continue',
      canImport: true,
      message: 'Figma token confirmed.',
      statusCode: result.status,
      tokenLabel,
    }
  }

  if (result.status === 401 || result.status === 403) {
    return {
      status: 'expired',
      action: 'replace_token',
      canImport: false,
      message: result.message || 'Figma token expired or unauthorized.',
      statusCode: result.status,
      tokenLabel,
    }
  }

  if (result.status === 429) {
    return {
      status: 'rate_limited',
      action: 'retry_later',
      canImport: false,
      message: result.message || 'Figma is rate limited. Try again later.',
      statusCode: result.status,
      tokenLabel,
    }
  }

  if (result.status === null) {
    return {
      status: 'network_error',
      action: 'retry_later',
      canImport: false,
      message: result.message || 'Could not reach Figma.',
      statusCode: null,
      tokenLabel,
    }
  }

  return {
    status: 'invalid',
    action: 'replace_token',
    canImport: false,
    message: result.message || 'Figma token is invalid.',
    statusCode: result.status,
    tokenLabel,
  }
}

export function buildFigmaTokenControlFromImportError(
  error: string | undefined,
  token?: string,
): FigmaTokenControl | null {
  const text = error?.trim()
  if (!text) return null
  if (/\b(401|403)\b|unauthori[sz]ed|forbidden|expired/i.test(text)) {
    return buildFigmaTokenControlFromValidation(
      {
        ok: false,
        status: /\b401\b/.test(text) ? 401 : 403,
        message: 'Figma token expired or unauthorized. Paste a token again.',
      },
      token,
    )
  }
  if (/\b429\b|rate limit/i.test(text)) {
    return buildFigmaTokenControlFromValidation(
      {
        ok: false,
        status: 429,
        message: 'Figma is rate limited. Try again later.',
      },
      token,
    )
  }
  return null
}

async function readFigmaError(response: Response): Promise<string> {
  try {
    const payload = (await response.json()) as {
      err?: unknown
      message?: unknown
      status?: unknown
    }
    const message = payload.err ?? payload.message ?? payload.status
    return typeof message === 'string' ? message : response.statusText
  } catch {
    return response.statusText
  }
}

/**
 * Whether a Figma FRAME can be imported by this build.
 *
 * Its own predicate, deliberately. The import page used to gate this on
 * `isOpenPencilFigmaImportAvailable()` -- the `.fig` service's configuration
 * -- but the frame path never touches that service: it posts to
 * `/api/figma/proxy` with a Figma access token. One feature's configuration
 * was deciding another feature's existence, and on the appliance the frame
 * card was disabled for a reason that had nothing to do with frames
 * (measured 2026-08-24).
 *
 * The constraint that actually applies here is the network. Importing a frame
 * means reaching Figma, an external design service, which a closed-network
 * build cannot do by construction -- so the appliance discriminator is the
 * honest signal, and it is a fact about THIS path.
 */
export function isFigmaFrameImportAvailable(env?: {
  VITE_STUDIO_DISTRIBUTION?: string
}): boolean {
  return !isApplianceDistribution(env)
}

function shouldUseFigmaProxy(): boolean {
  const env = (import.meta as { env?: { MODE?: string } }).env
  return (
    env?.MODE !== 'test' &&
    typeof window !== 'undefined' &&
    typeof document !== 'undefined'
  )
}

async function studioAuthorizationHeaders(): Promise<Record<string, string>> {
  const accessToken = (await getStudioAccessToken())?.trim()
  if (!accessToken) {
    throw new Error('Studio authentication is required.')
  }
  return { Authorization: `Bearer ${accessToken}` }
}

async function validateTokenRequest(token: string): Promise<Response> {
  if (shouldUseFigmaProxy()) {
    return fetch('/api/figma/proxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await studioAuthorizationHeaders()),
      },
      body: JSON.stringify({
        kind: 'me',
        figmaAccessToken: token,
      }),
    })
  }

  return fetch('https://api.figma.com/v1/me', {
    headers: {
      Accept: 'application/json',
      'X-Figma-Token': token,
    },
  })
}

async function serverStoreTokenRequest(
  token: string,
  options: FigmaTokenServerStoreOptions = {},
): Promise<Response> {
  const fetcher = options.fetchImpl ?? fetch
  return fetcher('/api/figma/proxy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await studioAuthorizationHeaders()),
    },
    body: JSON.stringify({
      kind: 'store-token',
      workspaceId: options.workspaceId ?? 'workspace_local',
      figmaAccessToken: token,
    }),
  })
}

async function serverTokenStatusRequest(
  options: FigmaTokenServerStoreOptions = {},
): Promise<Response> {
  const fetcher = options.fetchImpl ?? fetch
  return fetcher('/api/figma/proxy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await studioAuthorizationHeaders()),
    },
    body: JSON.stringify({
      kind: 'token-status',
      workspaceId: options.workspaceId ?? 'workspace_local',
    }),
  })
}

export async function readServerFigmaTokenStatus(
  options: FigmaTokenServerStoreOptions = {},
): Promise<FigmaTokenValidationResult> {
  try {
    const response = await serverTokenStatusRequest(options)
    const payload = (await response.json().catch(() => ({}))) as {
      status?: string
      statusCode?: number | null
      message?: string
      tokenRef?: string
      tokenLabel?: string
      authenticated?: boolean
    }
    const ok = response.ok && payload.status === 'ready'
    if (payload.status === 'missing') {
      const result = {
        ok: false,
        status: null,
        message: payload.message ?? 'No Figma token is connected.',
      }
      return {
        ...result,
        authenticated: payload.authenticated,
        control: buildFigmaTokenControlFromStoredToken(undefined),
      }
    }
    const result = {
      ok,
      status: payload.statusCode ?? response.status,
      message: payload.message ?? (ok ? 'Connected' : 'No Figma token is connected.'),
    }
    return {
      ...result,
      tokenRef: payload.tokenRef,
      tokenLabel: payload.tokenLabel,
      authenticated: payload.authenticated,
      control: buildFigmaTokenControlFromValidation(
        result,
        undefined,
        payload.tokenLabel,
      ),
    }
  } catch (error) {
    const result = {
      ok: false,
      status: null,
      message: error instanceof Error ? error.message : String(error),
    }
    return {
      ...result,
      control: buildFigmaTokenControlFromValidation(result),
    }
  }
}

export async function storeFigmaAccessTokenOnServer(
  token: string,
  options: FigmaTokenServerStoreOptions = {},
): Promise<FigmaTokenValidationResult> {
  const trimmed = token.trim()
  if (!trimmed) {
    const result = {
      ok: false,
      status: null,
      message: 'Missing Figma token.',
    }
    return {
      ...result,
      control: buildFigmaTokenControlFromValidation(result, trimmed),
    }
  }

  try {
    const response = await serverStoreTokenRequest(trimmed, options)
    const payload = (await response.json().catch(() => ({}))) as {
      status?: string
      statusCode?: number | null
      message?: string
      tokenRef?: string
      tokenLabel?: string
      authenticated?: boolean
      error?: string
    }
    const ok = response.ok && payload.status === 'ready'
    const result = {
      ok,
      status: payload.statusCode ?? response.status,
      message: payload.message ?? payload.error ?? (ok ? 'Connected' : 'Figma token is invalid.'),
    }
    return {
      ...result,
      tokenRef: payload.tokenRef,
      tokenLabel: payload.tokenLabel,
      authenticated: payload.authenticated,
      control: buildFigmaTokenControlFromValidation(
        result,
        trimmed,
        payload.tokenLabel,
      ),
    }
  } catch (error) {
    const result = {
      ok: false,
      status: null,
      message: error instanceof Error ? error.message : String(error),
    }
    return {
      ...result,
      control: buildFigmaTokenControlFromValidation(result, trimmed),
    }
  }
}

export async function validateFigmaAccessToken(
  token: string,
): Promise<FigmaTokenValidationResult> {
  const trimmed = token.trim()
  if (!trimmed) {
    const result = {
      ok: false,
      status: null,
      message: 'Missing Figma token.',
    }
    return {
      ...result,
      control: buildFigmaTokenControlFromValidation(result, trimmed),
    }
  }

  try {
    const response = await validateTokenRequest(trimmed)
    if (response.ok) {
      const result = {
        ok: true,
        status: response.status,
        message: 'Connected',
      }
      return {
        ...result,
        control: buildFigmaTokenControlFromValidation(result, trimmed),
      }
    }
    const result = {
      ok: false,
      status: response.status,
      message: await readFigmaError(response),
    }
    return {
      ...result,
      control: buildFigmaTokenControlFromValidation(result, trimmed),
    }
  } catch (error) {
    const result = {
      ok: false,
      status: null,
      message: error instanceof Error ? error.message : String(error),
    }
    return {
      ...result,
      control: buildFigmaTokenControlFromValidation(result, trimmed),
    }
  }
}
